import { yupResolver } from "@hookform/resolvers/yup";
import { IncomingChallanBulkValidationSchema } from "../../utils/validations/validations";
import {
  useFieldArray,
  useForm,
  useWatch,
  Control,
  FieldErrors,
} from "react-hook-form";
import InputSearchableDropdown from "../inventory/InputSearchableDropdown";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPartDropdownOptions, getParties } from "../../api/inventory";
import LoadingComponent from "../../LoadingComponent";
import {
  toastBgState,
  toastMessageState,
  toastShowState,
} from "../../utils/atoms/atoms";
import { useSetRecoilState } from "recoil";
import { useEffect, useState } from "react";
import {
  IInputSearchableDropdown,
  IncomingChallanBulkFormValues,
} from "../../interfaces/app_types";
import { companyTypeValues, sentRecdStatus } from "../../utils/dropdown-values";
import FormField from "../common/FormField";
import { createIncomingBulkChallan } from "../../api/incoming";
import { closeModal } from "../../utils/funcs/common";

const DEVIATION_THRESHOLD_PCT = 0.5;
const FAILED_CHALLAN_DRAFTS_KEY = "incoming-challan-failed-drafts";

const getLocalToday = () => {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return localDate.toISOString().split("T")[0];
};

type FailedChallanDraft = {
  id: string;
  savedAt: string;
  values: IncomingChallanBulkFormValues;
};

const readFailedDrafts = (): FailedChallanDraft[] => {
  try {
    const value = localStorage.getItem(FAILED_CHALLAN_DRAFTS_KEY);
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((draft) => draft?.values && draft?.savedAt).slice(0, 2)
      : [];
  } catch {
    return [];
  }
};

// One row per selected part. Isolated in its own component so only the row
// whose quantity/weight changed re-renders, and so it can watch its own
// fields without re-rendering the whole part list.
const PartRow = ({
  index,
  field,
  control,
  register,
  errors,
  remove,
}: {
  index: number;
  field: IncomingChallanBulkFormValues["parts"][number] & { id: string };
  control: Control<IncomingChallanBulkFormValues>;
  register: any;
  errors: FieldErrors<IncomingChallanBulkFormValues>;
  remove: (index: number) => void;
}) => {
  const quantity = useWatch({ control, name: `parts.${index}.quantity` });
  const weight = useWatch({ control, name: `parts.${index}.weight` });
  const watchedPart = useWatch({ control, name: `parts.${index}.part` });
  const statusValue = useWatch({ control, name: "status.value" as any });

  const part = watchedPart || field.part;
  let perPieceWeight = field.perPieceWeight;
  const isRecd = statusValue === 1;

  let rangeText: string | null = null;
  let checkMessage: { ok: boolean; text: string } | null = null;

  if (isRecd) {
    if (!perPieceWeight) {
      checkMessage = {
        ok: false,
        text: "Warning: No per-piece weight is available for comparison",
      };
    } else if (typeof quantity === "number" && quantity > 0) {
      perPieceWeight = perPieceWeight / 1000; // Convert grams to kg
      const low =
        quantity * perPieceWeight * (1 - DEVIATION_THRESHOLD_PCT / 100);
      const high =
        quantity * perPieceWeight * (1 + DEVIATION_THRESHOLD_PCT / 100);
      rangeText = `Accepted weight: ${low.toFixed(2)}–${high.toFixed(2)} kg`;

      if (typeof weight === "number" && weight > 0) {
        const expectedQty = weight / perPieceWeight;
        checkMessage =
          weight >= low && weight <= high
            ? {
                ok: true,
                text: `Okay (≈${expectedQty.toFixed(1)} pcs by weight)`,
              }
            : {
                ok: false,
                text: `Warning: Weight suggests ≈${expectedQty.toFixed(
                  1
                )} pcs, not ${quantity}. You can still submit this entry.`,
              };
      }
    }
  }

  const partErrors = errors?.parts?.[index] as any;

  return (
    <div className="border rounded p-2 mt-2">
      <div className="d-flex flex-wrap align-items-start gap-2">
        <div className="fw-bold" style={{ minWidth: "180px" }}>
          {part?.label ||
            (part?.value ? `Part #${part.value}` : "Loading selected part…")}
        </div>
        <FormField
          title="Quantity"
          value=""
          fieldType="number"
          formRegister={{
            ...register(`parts.${index}.quantity`, { valueAsNumber: true }),
          }}
          errorMessage={partErrors?.quantity?.message}
        />
        <FormField
          title="Weight"
          value=""
          fieldType="number"
          formRegister={{
            ...register(`parts.${index}.weight`, { valueAsNumber: true }),
          }}
          errorMessage={partErrors?.weight?.message}
        />
        <FormField
          title="Remarks"
          value={field.remarks}
          formRegister={{ ...register(`parts.${index}.remarks`) }}
          errorMessage={partErrors?.remarks?.message}
        />
        <button
          type="button"
          className="btn btn-sm btn-outline-danger mt-4"
          onClick={() => remove(index)}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
      {rangeText && <div className="small text-muted mt-1">{rangeText}</div>}
      {checkMessage && !partErrors?.quantity?.message && (
        <div
          className={`small mt-1 ${
            checkMessage.ok
              ? "text-success"
              : "alert alert-warning py-1 px-2 mb-0"
          }`}
        >
          {checkMessage.text}
        </div>
      )}
    </div>
  );
};
const IncomingChallanBulkFormBody = () => {
  const resolver = yupResolver(IncomingChallanBulkValidationSchema);

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [failedDrafts, setFailedDrafts] =
    useState<FailedChallanDraft[]>(readFailedDrafts);
  const [companyDisabled, setCompanyDisabled] = useState(false);

  const setToastShow = useSetRecoilState(toastShowState);
  const setToastBg = useSetRecoilState(toastBgState);
  const setToastMessage = useSetRecoilState(toastMessageState);
  const queryClient = useQueryClient();

  const {
    isLoading: isPartsLoading,
    data: inventory,
    isError: isPartError,
    isSuccess: isPartSuccess,
  } = useQuery({
    queryKey: ["part-dropdown-values"],
    queryFn: getPartDropdownOptions,
  });
  const {
    isLoading: isPartiesLoading,
    data: partiesOptions,
    isError: isPartiesError,
    isSuccess: isPartiesSuccess,
  } = useQuery({
    queryKey: ["parties"],
    queryFn: getParties,
  });

  const defaultValues: IncomingChallanBulkFormValues = {
    action_date: getLocalToday(),
    party: {} as any,
    status: {} as any,
    company: {} as any,
    partToAdd: null,
    parts: [],
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    resetField,
    watch,
    getValues,
    setValue,
  } = useForm<IncomingChallanBulkFormValues>({
    resolver,
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "parts",
  });

  const watchedParts = watch("parts");
  const watchedStatus = watch("status");
  const isSentEntry = watchedStatus?.value === 0;

  const saveFailedDraft = (values: IncomingChallanBulkFormValues) => {
    const draft: FailedChallanDraft = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      savedAt: new Date().toISOString(),
      values: JSON.parse(JSON.stringify({ ...values, partToAdd: null })),
    };
    setFailedDrafts((currentDrafts) => {
      const nextDrafts = [draft, ...currentDrafts].slice(0, 2);
      localStorage.setItem(
        FAILED_CHALLAN_DRAFTS_KEY,
        JSON.stringify(nextDrafts)
      );
      return nextDrafts;
    });
  };

  const restoreFailedDraft = (draft: FailedChallanDraft) => {
    const isSentDraft = draft.values.status?.value === 0;
    reset({
      ...draft.values,
      action_date: isSentDraft ? getLocalToday() : draft.values.action_date,
      partToAdd: null,
    });
    setCompanyDisabled(draft.values.status?.value === 1);
    setToastShow(true);
    setToastBg("success");
    setToastMessage("Saved challan data restored");
  };

  const addSentRecdMutation = useMutation({
    mutationFn: async ({
      payload,
    }: {
      payload: any;
      formValues: IncomingChallanBulkFormValues;
    }) => {
      return createIncomingBulkChallan(payload);
    },
    onSuccess() {
      closeModal("incomingChallanBulkModal");
      setToastShow(true);
      reset();
      setIsSubmitSuccessful(true);
      setToastBg("success");
      setToastMessage("Challan created");
      queryClient.invalidateQueries(["incoming-sent"]);
      queryClient.invalidateQueries(["incoming-recd"]);
      queryClient.invalidateQueries(["incoming-pending"]);
      queryClient.invalidateQueries(["incoming-challan"]);
    },
    onError(error: any, variables) {
      if (error.code === "PREVIOUS_CHALLAN_UNLOCKED") {
        saveFailedDraft(variables.formValues);
      }
      setToastShow(true);
      setToastBg("danger");
      setToastMessage(error.message || "Failed to create challan");
    },
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      setIsSubmitSuccessful(false);
    }
  }, [isSubmitSuccessful]);

  const handleStatusChange = (selectedStatus: {
    label: string;
    value: number;
  }) => {
    if (selectedStatus?.value === 0) {
      setCompanyDisabled(false);
      setValue("action_date", getLocalToday(), { shouldValidate: true });
    } else if (selectedStatus?.value === 1) {
      setCompanyDisabled(true);
      resetField("company");
    }
  };

  const handleAddPart = (selectedOption: any) => {
    if (selectedOption?.value == null) return;

    const inventoryOption = (inventory || []).find(
      (option: any) => option.value === selectedOption.value
    );
    const partLabel = selectedOption.label || inventoryOption?.label;

    if (!partLabel) {
      setToastShow(true);
      setToastBg("danger");
      setToastMessage("This part is missing its display label. Please retry.");
      resetField("partToAdd");
      return;
    }

    const currentStatus = getValues("status");
    const isRecd = currentStatus?.value === 1;

    append({
      part: { value: selectedOption.value, label: partLabel },
      quantity: "",
      weight: "",
      // Recd remarks are a different kind of note than the fixed
      // color/spec remark, so don't prefill it there.
      remarks: isRecd
        ? ""
        : selectedOption.incoming_remark ||
          inventoryOption?.incoming_remark ||
          "",
      perPieceWeight:
        selectedOption.per_piece_weight ??
        inventoryOption?.per_piece_weight ??
        null,
    });
    resetField("partToAdd");
  };

  const availablePartOptions = (inventory || []).filter(
    (opt: any) => !watchedParts?.some((p) => p.part?.value === opt.value)
  );

  const isLoading = isPartiesLoading && isPartsLoading;
  const isError = isPartiesError && isPartError;
  const isSuccess = isPartiesSuccess && isPartSuccess;

  return (
    <LoadingComponent
      isError={isError}
      isSuccess={isSuccess}
      isLoading={isLoading}
      ComponentBody={
        <div>
          {failedDrafts.length > 0 && (
            <div className="border rounded bg-light p-2 m-2">
              <div className="small fw-bold mb-2">
                Restore a failed challan submission
              </div>
              <div className="d-flex flex-wrap gap-2">
                {failedDrafts.map((draft) => (
                  <button
                    key={draft.id}
                    type="button"
                    className="btn btn-sm btn-outline-secondary text-start"
                    onClick={() => restoreFailedDraft(draft)}
                  >
                    <span className="fw-bold">
                      {draft.values.company?.label || "Received entry"}
                    </span>
                    <span className="d-block small">
                      {draft.values.party?.label || "No party"} ·{" "}
                      {draft.values.parts?.length || 0} item(s)
                    </span>
                    <span className="d-block small text-muted">
                      {new Date(draft.savedAt).toLocaleString()}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <form
            onSubmit={handleSubmit((data, event) => {
              event?.preventDefault();

              if (addSentRecdMutation.isLoading) return;

              const payload = {
                action_date: data.action_date,
                party: data.party,
                status: data.status,
                company: data.company,
                part_ids: data.parts.map((p) => p.part),
                quantities: data.parts.map((p) => p.quantity).join(","),
                weights: data.parts.map((p) => p.weight).join(","),
                remarks: data.parts.map((p) => p.remarks).join(","),
              };

              addSentRecdMutation.mutate({
                payload,
                formValues: data,
              });
            })}
          >
            <InputSearchableDropdown
              name="status"
              selectedValue={{} as IInputSearchableDropdown}
              options={sentRecdStatus}
              handleSelectChange={handleStatusChange}
              isAsync={true}
              control={control}
              errorMessage={(errors as any)?.status?.value?.message}
            />
            <InputSearchableDropdown
              name="company"
              selectedValue={{} as IInputSearchableDropdown}
              disabled={companyDisabled}
              errorMessage={(errors as any)?.company?.value?.message}
              isMulti={false}
              isColor={true}
              control={control}
              options={companyTypeValues}
            />
            <FormField
              title="Select Date"
              fieldType="date"
              readOnly={isSentEntry}
              formRegister={{ ...register("action_date") }}
              errorMessage={errors?.action_date?.message}
            />

            <div className="p-2">
              <InputSearchableDropdown
                name="party"
                isAsync={true}
                isColor={true}
                isMulti={false}
                selectedValue={{} as IInputSearchableDropdown}
                options={partiesOptions}
                control={control}
                errorMessage={(errors as any)?.party?.value?.message}
              />

              <InputSearchableDropdown
                name="partToAdd"
                selectedValue={{} as IInputSearchableDropdown}
                options={availablePartOptions}
                isAsync={true}
                isMulti={false}
                control={control}
                handleSelectChange={handleAddPart}
              />
              {(errors as any)?.parts?.message && (
                <div className="text-danger small mt-1">
                  {(errors as any).parts.message}
                </div>
              )}

              {fields.map((field, index) => (
                <PartRow
                  key={field.id}
                  index={index}
                  field={field as any}
                  control={control}
                  register={register}
                  errors={errors}
                  remove={remove}
                />
              ))}
            </div>

            <button
              type="submit"
              className="styled-btn--dark--bold m-2"
              disabled={addSentRecdMutation.isLoading}
            >
              {addSentRecdMutation.isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Saving...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </form>
        </div>
      }
    />
  );
};

export default IncomingChallanBulkFormBody;
